/**
 * Status utilities for truck scheduling operations
 * Provides status handling for both inbound and outbound truck operations
 * Supports domain-specific status flows while sharing common logic
 */

// ===== INBOUND STATUS CONSTANTS =====
const INBOUND_STATUS = {
  // Shared statuses
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
  
  // Inbound specific statuses from schema enum
  ARRIVED: 'Arrived',
  RECEIVED: 'Received',
  PUTAWAY: 'Putaway',
  COMPLETED: 'Completed'
};

// ===== OUTBOUND STATUS CONSTANTS =====
const OUTBOUND_STATUS = {
  // Shared statuses
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
  
  // Outbound specific statuses from schema enum
  STAGED: 'Staged',
  LOADED: 'Loaded',
  SHIPPED: 'Shipped',
  COMPLETED: 'Completed'
};

// ===== STATUS UTILITIES =====

/**
 * Get domain-specific status constants
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Object} - Domain-specific status constants
 */
const getStatusConstants = (domain) => {
  return domain === 'inbound' ? INBOUND_STATUS : OUTBOUND_STATUS;
};

/**
 * Get status display names for the specified domain
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Object} - Status display names
 */
const getStatusDisplayMap = (domain) => {
  const STATUS = getStatusConstants(domain);
  
  // Status display names for UI - matches schema enums
  return {
    // Shared statuses
    [STATUS.SCHEDULED]: 'Scheduled',
    [STATUS.CANCELLED]: 'Cancelled',
    
    // Domain-specific statuses
    ...(domain === 'inbound' ? {
      [STATUS.ARRIVED]: 'Arrived',
      [STATUS.RECEIVED]: 'Received',
      [STATUS.PUTAWAY]: 'Putaway',
      [STATUS.COMPLETED]: 'Completed'
    } : {
      [STATUS.STAGED]: 'Staged',
      [STATUS.LOADED]: 'Loaded',
      [STATUS.SHIPPED]: 'Shipped',
      [STATUS.COMPLETED]: 'Completed'
    })
  };
};

/**
 * Get status colors for badges
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Object} - Status colors
 */
const getStatusColorMap = (domain) => {
  const STATUS = getStatusConstants(domain);
  
  // Status badge colors for UI - matches schema status values
  return {
    // Shared statuses
    [STATUS.SCHEDULED]: 'bg-blue-100 text-blue-800',
    [STATUS.CANCELLED]: 'bg-red-100 text-red-800',
    
    // Domain-specific statuses
    ...(domain === 'inbound' ? {
      [STATUS.ARRIVED]: 'bg-yellow-100 text-yellow-800',
      [STATUS.RECEIVED]: 'bg-purple-100 text-purple-800',
      [STATUS.PUTAWAY]: 'bg-indigo-100 text-indigo-800',
      [STATUS.COMPLETED]: 'bg-green-100 text-green-800'
    } : {
      [STATUS.STAGED]: 'bg-green-100 text-green-800',
      [STATUS.LOADED]: 'bg-teal-100 text-teal-800',
      [STATUS.SHIPPED]: 'bg-blue-100 text-blue-800',
      [STATUS.COMPLETED]: 'bg-green-100 text-green-800'
    })
  };
};

/**
 * Get timestamp fields for statuses
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Object} - Status timestamp fields
 */
const getStatusTimestampFieldMap = (domain) => {
  const STATUS = getStatusConstants(domain);
  
  // Status related timestamp fields - matches schema timestamp fields
  return {
    // Shared
    [STATUS.SCHEDULED]: 'scheduled_at',
    [STATUS.CANCELLED]: 'cancelled_at',

    // Domain-specific
    ...(domain === 'inbound' ? {
      [STATUS.ARRIVED]: 'arrived_at',
      [STATUS.RECEIVED]: 'received_at',
      [STATUS.PUTAWAY]: 'putaway_at',
      [STATUS.COMPLETED]: 'completed_at'
    } : {
      [STATUS.STAGED]: 'staged_at',
      [STATUS.LOADED]: 'loaded_at',
      [STATUS.SHIPPED]: 'shipped_at',
      [STATUS.COMPLETED]: 'completed_at'
    })
  };
};

/**
 * Get status transitions map
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Object} - Valid status transitions
 */
const getStatusTransitionsMap = (domain) => {
  const STATUS = getStatusConstants(domain);
  
  return domain === 'inbound' ? {
    // Inbound transitions based on schema
    [STATUS.SCHEDULED]: [STATUS.ARRIVED, STATUS.CANCELLED],
    [STATUS.ARRIVED]: [STATUS.RECEIVED, STATUS.CANCELLED],
    [STATUS.RECEIVED]: [STATUS.PUTAWAY, STATUS.CANCELLED],
    [STATUS.PUTAWAY]: [STATUS.COMPLETED, STATUS.CANCELLED],
    [STATUS.COMPLETED]: [],
    [STATUS.CANCELLED]: []
  } : {
    // Outbound transitions based on schema
    [STATUS.SCHEDULED]: [STATUS.STAGED, STATUS.CANCELLED],
    [STATUS.STAGED]: [STATUS.LOADED, STATUS.CANCELLED],
    [STATUS.LOADED]: [STATUS.SHIPPED, STATUS.CANCELLED],
    [STATUS.SHIPPED]: [STATUS.COMPLETED, STATUS.CANCELLED],
    [STATUS.COMPLETED]: [],
    [STATUS.CANCELLED]: []
  };
};

/**
 * Get all statuses in correct sequence
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Array} - Array of status objects
 */
const getStatusSequence = (domain) => {
  const STATUS = getStatusConstants(domain);
  const STATUS_DISPLAY = getStatusDisplayMap(domain);
  const STATUS_TIMESTAMP_FIELDS = getStatusTimestampFieldMap(domain);
  
  return domain === 'inbound' ? [
    { 
      key: STATUS.SCHEDULED, 
      label: STATUS_DISPLAY[STATUS.SCHEDULED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.SCHEDULED]
    },
    { 
      key: STATUS.ARRIVED, 
      label: STATUS_DISPLAY[STATUS.ARRIVED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.ARRIVED]
    },
    { 
      key: STATUS.RECEIVED, 
      label: STATUS_DISPLAY[STATUS.RECEIVED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.RECEIVED]
    },
    { 
      key: STATUS.PUTAWAY, 
      label: STATUS_DISPLAY[STATUS.PUTAWAY], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.PUTAWAY]
    },
    { 
      key: STATUS.COMPLETED, 
      label: STATUS_DISPLAY[STATUS.COMPLETED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.COMPLETED]
    }
  ] : [
    { 
      key: STATUS.SCHEDULED, 
      label: STATUS_DISPLAY[STATUS.SCHEDULED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.SCHEDULED]
    },
    { 
      key: STATUS.STAGED, 
      label: STATUS_DISPLAY[STATUS.STAGED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.STAGED]
    },
    { 
      key: STATUS.LOADED, 
      label: STATUS_DISPLAY[STATUS.LOADED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.LOADED]
    },
    { 
      key: STATUS.SHIPPED, 
      label: STATUS_DISPLAY[STATUS.SHIPPED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.SHIPPED]
    },
    { 
      key: STATUS.COMPLETED, 
      label: STATUS_DISPLAY[STATUS.COMPLETED], 
      timestampField: STATUS_TIMESTAMP_FIELDS[STATUS.COMPLETED]
    }
  ];
};

/**
 * Get formatted status display text
 * @param {string} status - The status code
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {string} - Human readable status
 */
const getStatusDisplay = (status, domain) => {
  const STATUS_DISPLAY = getStatusDisplayMap(domain);
  return STATUS_DISPLAY[status] || 'Unknown';
};

/**
 * Get CSS class for status badge
 * @param {string} status - The status code
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {string} - CSS classes for badge
 */
const getStatusColor = (status, domain) => {
  const STATUS_COLORS = getStatusColorMap(domain);
  return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Check if a status is considered "active" (needs attention)
 * @param {string} status - The status code
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {boolean} - Whether the status is active
 */
const isActiveStatus = (status, domain) => {
  const STATUS = getStatusConstants(domain);
  
  return domain === 'inbound' ? [
    STATUS.ARRIVED, 
    STATUS.RECEIVED,
    STATUS.PUTAWAY
  ].includes(status) : [
    STATUS.STAGED,
    STATUS.LOADED,
    STATUS.SHIPPED
  ].includes(status);
};

/**
 * Check if a status transition is valid
 * @param {string} currentStatus - Current status
 * @param {string} newStatus - Target status
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {boolean} - Whether the transition is valid
 */
const isValidTransition = (currentStatus, newStatus, domain) => {
  const validTransitions = getStatusTransitionsMap(domain);
  return validTransitions[currentStatus]?.includes(newStatus) || false;
};

/**
 * Get next available status options based on current status
 * @param {string} currentStatus - Current status
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Array} - Array of valid next statuses
 */
const getNextStatusOptions = (currentStatus, domain) => {
  const validTransitions = getStatusTransitionsMap(domain);
  return validTransitions[currentStatus] || [];
};

/**
 * Determine current status based on status field and fallback to timestamps
 * @param {Object} truck - Truck data object
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {string} - Current status key
 */
const determineCurrentStatus = (truck, domain = 'inbound') => {
  if (!truck) return 'Scheduled';
  
  const STATUS = getStatusConstants(domain);
  
  // Use status field as primary source
  if (truck.status) {
    const normalizedStatus = normalizeStatusValue(truck.status, domain);
    
    // Use the normalized status if valid
    if (normalizedStatus !== STATUS.SCHEDULED || truck.status === STATUS.SCHEDULED) {
      return normalizedStatus;
    }
    
    // If status field isn't recognized, fall back to timestamps
  }
  
  // Fall back to determining status from timestamps
  
  // Helper function to check if a date is valid
  const isValidDate = (date) => {
    if (!date) return false;
    const d = new Date(date);
    return !isNaN(d.getTime());
  };
  
  if (domain === 'inbound') {
    // Inbound timestamp fields, in reverse order of status progression
    if (truck.completed_at && isValidDate(truck.completed_at)) return STATUS.COMPLETED;
    if (truck.putaway_at && isValidDate(truck.putaway_at)) return STATUS.PUTAWAY;
    if (truck.started_at && isValidDate(truck.started_at)) return STATUS.RECEIVED;
    if (truck.arrival_datetime && isValidDate(truck.arrival_datetime)) return STATUS.ARRIVED;
  } else {
    // Outbound timestamp fields, in reverse order of status progression
    if (truck.completed_at && isValidDate(truck.completed_at)) return STATUS.COMPLETED;
    if (truck.departure_datetime && isValidDate(truck.departure_datetime)) return STATUS.SHIPPED;
    if (truck.loading_complete && isValidDate(truck.loading_complete)) return STATUS.LOADED;
    if (truck.staging_start && isValidDate(truck.staging_start)) return STATUS.STAGED;
  }
  
  return STATUS.SCHEDULED;
};

/**
 * Normalizes a status value to the canonical form
 * @param {String} status - The status value to normalize
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {String} Normalized status from STATUS constants
 */
const normalizeStatusValue = (status, domain) => {
  if (!status) return 'Scheduled';
  
  const STATUS = getStatusConstants(domain);
  
  // If already a canonical form, return as is
  if (Object.values(STATUS).includes(status)) {
    return status;
  }
  
  // Try to match by lowercasing
  const statusLower = status.toLowerCase();
  
  // Find matching STATUS key by comparing lowercase versions
  for (const key in STATUS) {
    if (STATUS[key].toLowerCase() === statusLower) {
      return STATUS[key];
    }
  }
  
  // Default to scheduled if no match
  console.warn(`normalizeStatusValue: Unrecognized status "${status}", defaulting to Scheduled`);
  return STATUS.SCHEDULED;
};

/**
 * Determines if a status change is a backward movement
 * @param {Object} truck - Truck object with current status
 * @param {String} targetStatus - Target status
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Boolean} Whether this is a backward movement
 */
const isBackwardMovement = (truck, targetStatus, domain = 'inbound') => {
  // Special handling for Cancelled status - it's not a backward movement
  if (targetStatus === 'Cancelled') return false;

  const currentStatus = determineCurrentStatus(truck, domain);
  const normalizedTarget = normalizeStatusValue(targetStatus, domain);

  const statusSequence = getStatusSequence(domain);
  const currentIndex = statusSequence.findIndex(s => s.key === currentStatus);
  const targetIndex = statusSequence.findIndex(s => s.key === normalizedTarget);

  // Safety check - if we can't find the statuses in the sequence
  if (currentIndex === -1 || targetIndex === -1) return false;

  return targetIndex < currentIndex;
};

/**
 * Get all timestamps that would be affected by a status change
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - Target status
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Array} - Array of timestamp fields that would be affected
 */
const getAffectedTimestamps = (fromStatus, toStatus, domain = 'inbound') => {
  // Special handling for Cancelled status
  if (toStatus === 'Cancelled') return [];

  const sequence = getStatusSequence(domain);

  const fromIndex = sequence.findIndex(s => s.key === fromStatus);
  const toIndex = sequence.findIndex(s => s.key === toStatus);

  // Safety check - if we can't find the statuses in the sequence
  if (fromIndex === -1 || toIndex === -1) return [];

  // If moving forward, no timestamps are cleared
  if (toIndex > fromIndex) return [];

  // If moving backward, all timestamps after toIndex are cleared
  return sequence.slice(toIndex + 1, fromIndex + 1).map(s => s.timestampField);
};

/**
 * Validate chronological order of timestamps
 * @param {Object} truck - Truck data with timestamps
 * @param {string} domain - 'inbound' or 'outbound'
 * @returns {Object} - Object with isValid flag and any errors
 */
const validateTimestampOrder = (truck, domain = 'inbound') => {
  const sequence = getStatusSequence(domain);
  const errors = [];

  // Track the last timestamp we've seen
  let lastTimestamp = null;
  let lastField = null;

  // Check each timestamp in sequence
  for (const status of sequence) {
    const timestampField = status.timestampField;
    const timestamp = truck[timestampField];

    // Skip if this timestamp doesn't exist
    if (!timestamp) continue;

    // Convert to Date object
    const timestampDate = new Date(timestamp);

    // Check if it's a valid date
    if (isNaN(timestampDate.getTime())) {
      errors.push({
        field: timestampField,
        message: `Invalid date format for ${status.label}`
      });
      continue;
    }

    // Check if it's after the previous timestamp
    if (lastTimestamp && timestampDate < lastTimestamp) {
      errors.push({
        field: timestampField,
        message: `${status.label} time (${timestampDate.toLocaleString()}) is earlier than ${lastField} (${lastTimestamp.toLocaleString()})`
      });
    }

    // Update the last timestamp
    lastTimestamp = timestampDate;
    lastField = status.label;
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Standardizes status operations for trucks
 * Enhanced with improved timestamp sequence validation and handling
 * @param {Object} truck - The truck object
 * @param {String} targetStatus - The status to change to
 * @param {String} domain - 'inbound' or 'outbound'
 * @param {Boolean} isBackward - Whether this is a backward movement (optional)
 * @returns {Object} Updated truck object with proper fields set
 */
const standardizeStatusChange = (truck, targetStatus, domain = 'inbound', isBackward = false) => {
  if (!truck || !targetStatus) {
    console.error('standardizeStatusChange: Missing required parameters', { truck, targetStatus });
    return { ...truck }; // Return unchanged if missing params
  }

  // Create a clean copy of the truck
  const updatedTruck = { ...truck };

  // Special handling for Cancelled status
  if (targetStatus === 'Cancelled') {
    updatedTruck.status = 'Cancelled';
    // Create a standardized ISO format without milliseconds for database compatibility
    const cancelDate = new Date();
    updatedTruck.cancelled_at = cancelDate.toISOString().slice(0, 19).replace('T', ' ');
    console.log(`standardizeStatusChange: Truck cancelled with timestamp ${updatedTruck.cancelled_at}`);
    return updatedTruck;
  }

  // Normalize the target status to canonical form
  const normalizedStatus = normalizeStatusValue(targetStatus, domain);

  // Get current status for comparison
  const currentStatus = determineCurrentStatus(truck, domain);

  // If not explicitly backward, determine if it's a backward movement
  if (!isBackward) {
    isBackward = isBackwardMovement(truck, normalizedStatus, domain);
  }

  console.log(`standardizeStatusChange: ${domain} truck status change from ${currentStatus} to ${normalizedStatus}, backward: ${isBackward}`);

  // Always set the status field explicitly
  updatedTruck.status = normalizedStatus;

  // Get domain-specific constants and mappings
  const STATUS_TIMESTAMP_FIELDS = getStatusTimestampFieldMap(domain);

  // Map statuses to timestamp fields
  const targetField = STATUS_TIMESTAMP_FIELDS[normalizedStatus];

  // Get the full sequence of status timestamps for validation
  const statusSequence = getStatusSequence(domain);
  const allTimestampFields = statusSequence.map(s => s.timestampField);

  // Handle timestamp sequence enforcement
  if (!isBackward) {
    // FORWARD PROGRESS: Ensure all previous timestamps exist and are in correct order

    // Find the index of current and target status in the sequence
    const targetIndex = statusSequence.findIndex(s => s.key === normalizedStatus);

    // For each status up to target, ensure timestamp exists in correct order
    let lastTimestamp = null;

    for (let i = 0; i <= targetIndex; i++) {
      const timestampField = statusSequence[i].timestampField;

      // Skip 'cancelled_at' field
      if (timestampField === 'cancelled_at') continue;

      // If we're at the target field, always set it to now
      if (timestampField === targetField) {
        // Use the most recent time, ensuring it's at least 1 second after the previous timestamp
        const now = new Date();
        if (lastTimestamp) {
          // Ensure target timestamp is after the previous one
          updatedTruck[timestampField] = new Date(Math.max(
            lastTimestamp.getTime() + 1000, // At least 1 second after previous
            now.getTime() // But not earlier than now
          )).toISOString();
        } else {
          updatedTruck[timestampField] = now.toISOString();
        }
        break;
      }

      // For previous timestamps, if missing, backfill logically
      if (!updatedTruck[timestampField]) {
        const previousTimestamp = lastTimestamp ?
          new Date(lastTimestamp.getTime() - 1000) : // 1 second before last timestamp
          new Date(Date.now() - ((targetIndex - i + 1) * 1000)); // Staggered before now

        console.log(`standardizeStatusChange: Backfilling missing timestamp for ${statusSequence[i].key}`);
        updatedTruck[timestampField] = previousTimestamp.toISOString();
      }

      // Update lastTimestamp for next iteration
      lastTimestamp = new Date(updatedTruck[timestampField]);
    }
  } else {
    // BACKWARD MOVEMENT: Clear all subsequent timestamps
    const timestampsToNullify = getAffectedTimestamps(currentStatus, normalizedStatus, domain);

    timestampsToNullify.forEach(field => {
      updatedTruck[field] = null;
    });

    console.log(`standardizeStatusChange: Nullified fields for backward movement:`, timestampsToNullify);

    // Ensure the target status timestamp exists
    if (targetField && !updatedTruck[targetField]) {
      updatedTruck[targetField] = new Date().toISOString();
    }
  }

  return updatedTruck;
};

export {
  INBOUND_STATUS,
  OUTBOUND_STATUS,
  getStatusConstants,
  getStatusDisplayMap,
  getStatusColorMap,
  getStatusTimestampFieldMap,
  getStatusSequence,
  getStatusDisplay,
  getStatusColor,
  isActiveStatus,
  isValidTransition,
  getNextStatusOptions,
  determineCurrentStatus,
  normalizeStatusValue,
  isBackwardMovement,
  getAffectedTimestamps,
  validateTimestampOrder,
  standardizeStatusChange
};